import type { Account, ContactEntry, ConversationSummary, Id, RequestEntry } from '@/model';
import type { ContactNote, Lookup, NoteField, NotePatch, PhoneRegion } from './types';
import regions from './phone-regions.json';
import { accountDisplayName } from '@/core/hooks';
export const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const chars = (value: string) => [...value].length;
export const normalize = (text: string) => text.normalize('NFKC').normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase().trim();
export const noteFields: NoteField[] = ['mobile', 'email', 'company', 'department', 'location'];
export const phoneRegions: readonly PhoneRegion[] = regions;
export const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
export function pending(request: {
  status: string;
  expires_at?: string;
}, now = Date.now()): boolean {
  return request.status === 'pending' && (request.expires_at === undefined || Date.parse(request.expires_at) > now);
}
export function relationship(id: Id, me: Id, contacts: ContactEntry[], incoming: RequestEntry[], outgoing: RequestEntry[], blocked: Id[]) {
  if (id === me) return {
    kind: 'self'
  } as const;
  if (blocked.includes(id)) return {
    kind: 'blocked'
  } as const;
  if (contacts.some(c => c.account.id === id)) return {
    kind: 'contact'
  } as const;
  const received = incoming.find(r => r.account.id === id && pending(r.request));
  if (received) return {
    kind: 'incoming',
    requestId: received.request.id
  } as const;
  const sent = outgoing.find(r => r.account.id === id && pending(r.request));
  if (sent) return {
    kind: 'outgoing',
    requestId: sent.request.id
  } as const;
  return {
    kind: 'none'
  } as const;
}
export type Relationship = ReturnType<typeof relationship>;
export function shownName(account: Account, notes: readonly Partial<ContactNote>[] = [], alias?: string | null): string {
  return accountDisplayName(account, notes.find(note => note.account_id === account.id)?.alias || alias || undefined);
}
export function sections(contacts: ContactEntry[], notes: readonly Partial<ContactNote>[], query: string, chatsOnly: boolean, labels: Id[], links: ReadonlyMap<Id, Id[]>, match: 'any' | 'all', locale: string) {
  const collator = new Intl.Collator(locale, {
    sensitivity: 'base',
    numeric: true
  });
  const filtered = contacts.filter(c => {
    const note = notes.find(n => n.account_id === c.account.id);
    const tags = links.get(c.account.id) ?? [];
    return (!chatsOnly || note?.chats_only === true) && (!labels.length || (match === 'all' ? labels.every(id => tags.includes(id)) : labels.some(id => tags.includes(id)))) && [shownName(c.account, notes, c.contact.alias), c.account.display_name, c.account.handle, String(c.account.account_number ?? '')].some(name => normalize(name).includes(normalize(query)));
  }).sort((a, b) => collator.compare(shownName(a.account, notes, a.contact.alias), shownName(b.account, notes, b.contact.alias)) || a.account.id.localeCompare(b.account.id));
  const groups = new Map<string, ContactEntry[]>();
  for (const c of filtered) {
    const name = normalize(shownName(c.account, notes, c.contact.alias));
    const key = notes.find(n => n.account_id === c.account.id)?.starred ? 'starred' : /^[a-z]/.test(name) ? name[0]!.toUpperCase() : '#';
    groups.set(key, [...(groups.get(key) ?? []), c]);
  }
  const rank = (s: string) => s === 'starred' ? '0' : s === '#' ? '2' : '1' + s;
  return [...groups].sort(([a], [b]) => rank(a).localeCompare(rank(b))).map(([id, people]) => ({
    id,
    people
  }));
}
export function commonGroups(chats: ConversationSummary[], me: Id, person: Id) {
  return chats.filter((c, i) => chats.findIndex(x => x.id === c.id) === i && c.kind === 'group' && !c.left && !c.conversation.dissolved_at && [me, person].every(id => c.conversation.members.some(m => m.account_id === id && m.left_seq === null)));
}
export const clean = (s: string, singleLine = false) => (singleLine ? s.replace(/\p{Cc}/gu, ' ') : s).trim() || null;
export function cleanNotePatch(draft: {
  alias: string;
  note: string;
  fields: Partial<Record<NoteField, string>>;
}, original?: {
  alias?: string | null;
  note?: string | null;
  fields?: Partial<Record<NoteField, string>>;
}): NotePatch | null {
  const patch: NotePatch = {};
  const alias = clean(draft.alias, true),
    note = clean(draft.note);
  if (alias !== (original?.alias ?? null)) patch.alias = alias;
  if (note !== (original?.note ?? null)) patch.note = note;
  const fields: Partial<Record<NoteField, string | null>> = {};
  for (const key of noteFields) {
    const value = clean(draft.fields[key] ?? '', true);
    if (value !== (original?.fields?.[key] ?? null)) fields[key] = value;
  }
  if (Object.keys(fields).length) patch.fields = fields;
  return Object.keys(patch).length ? patch : null;
}
// Region facts are copied from HyloCore PhoneRegions+Table.swift (bundled metadata, no runtime library).
function asciiDigits(text: string): string {
  return [...text.normalize('NFKC')].map(c => {
    const n = c.codePointAt(0)!;
    if (n >= 0x660 && n <= 0x669) return String(n - 0x660);
    if (n >= 0x6f0 && n <= 0x6f9) return String(n - 0x6f0);
    return /[0-9]/.test(c) ? c : '';
  }).join('');
}
export function parseIdentifier(input: string, iso = 'US'): Lookup | null {
  const text = input.trim().normalize('NFKC');
  if (!text) return null;
  if (text.includes('@') && !/^@[a-z0-9_.]+$/i.test(text)) {
    const email = text.normalize('NFC').replace(/[A-Z]/g, c => c.toLowerCase());
    const [local, domain, ...rest] = email.split('@');
    return local && domain && !rest.length && chars(local) <= 64 && chars(email) <= 254 && domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.') && !/[\s\p{Cc}]/u.test(email) ? {
      email
    } : null;
  }
  const compact = text.replace(/[\s().\-\u2010-\u2014\u2212]/gu, '');
  const digits = asciiDigits(compact);
  if (digits.length === 8 && /^\p{Nd}+$/u.test(compact) && Number(digits) >= 10000000) return {
    number: Number(digits)
  };
  const region = phoneRegions.find(r => r.iso === iso) ?? phoneRegions[1]!;
  if (compact.startsWith('+') && /^\+\p{Nd}+$/u.test(compact) || digits.length >= 9 && /^\p{Nd}+$/u.test(compact)) {
    let phone: string;
    if (compact.startsWith('+')) phone = '+' + digits;else if (digits.startsWith(region.international_prefix)) phone = '+' + digits.slice(region.international_prefix.length);else if (region.trunk_prefix === null && region.national_lengths.includes(digits.length)) phone = '+' + region.calling_code + digits;else if (digits.startsWith(region.calling_code) && region.national_lengths.includes(digits.length - region.calling_code.length)) phone = '+' + digits;else phone = '+' + region.calling_code + (region.trunk_prefix && digits.startsWith(region.trunk_prefix) ? digits.slice(region.trunk_prefix.length) : digits);
    return /^\+[1-9][0-9]{6,14}$/.test(phone) ? {
      phone
    } : null;
  }
  const handle = text.replace(/^@/, '').toLowerCase();
  return /^[a-z0-9_.]{3,32}$/.test(handle) && /[a-z]/.test(handle) ? {
    handle
  } : null;
}
export function normalizeOrgCode(input: string, origin?: string): string | null {
  if (origin && /^(https?:|hylo:)/i.test(input.trim())) {
    try {
      const url = new URL(input.trim());
      if (url.protocol === 'hylo:' ? url.hostname !== 'org' || url.pathname.split('/').filter(Boolean).length !== 1 : url.origin !== origin || url.pathname !== '/org') return null;
      if (url.username || url.password || url.search) return null;
    } catch {
      return null;
    }
  }
  const raw = input.includes('#') ? input.slice(input.lastIndexOf('#') + 1) : input.replace(/^hylo:\/\/org\//, '');
  const code = raw.replace(/[\s-]/g, '').toUpperCase();
  return /^[A-HJ-NP-Z2-9]{12}$/.test(code) ? code : null;
}
export type ParsedLink = {
  kind: 'profile';
  handle: string;
} | {
  kind: 'contact' | 'group';
  token: string;
} | {
  kind: 'org';
  code: string;
};
export function parseLink(input: string, origin: string): ParsedLink | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'hylo:' && url.origin !== origin) return null;
  const native = url.protocol === 'hylo:';
  const path = native ? [url.hostname, ...url.pathname.split('/').filter(Boolean)] : url.pathname.split('/').filter(Boolean);
  if (path[0] === 'people' && path[1] === 'u') path.shift();
  if (url.username || url.password || url.search) return null;
  if (path[0] === 'u' && path.length === 2) {
    let handle: string;
    try {
      handle = decodeURIComponent(path[1] ?? '').replace(/^@/, '').toLowerCase();
    } catch {
      return null;
    }
    return /^[a-z0-9_.]{3,32}$/.test(handle) ? {
      kind: 'profile',
      handle
    } : null;
  }
  if (path[0] === 'org' && path.length === (native ? 2 : 1)) {
    const code = normalizeOrgCode(native ? path[1] ?? '' : url.hash.slice(1));
    return code ? {
      kind: 'org',
      code
    } : null;
  }
  const token = native ? path[1] ?? '' : url.hash.slice(1);
  if ((path[0] === 'add' || path[0] === 'join') && path.length === (native ? 2 : 1) && /^[A-Za-z0-9_-]{22}$/.test(token)) return {
    kind: path[0] === 'add' ? 'contact' : 'group',
    token
  };
  return null;
}
